<script setup lang="ts">
import {
  AccountModel,
  OpenAndUpcomingProductItemDto,
  OpenAndUpcomingSeasonAssortmentDto,
  ProductMainGroups,
} from '@onrunning/backstage-models';
import { ref, watch, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageButton from '@/components/common/BackstageButton.vue';
import { AllowableAssortmentService } from '@/services/allowable-assortment.service';
import { PricatService } from '@/services/pricat.service';
import LaunchCalendarTimelineItem from '@/components/launch-calendar/launch-calendar-timeline-item.vue';
import { type LaunchCalendarModel } from '@/models/launch-calendar.model';
import { type LaunchCalendarTimelineItemModel } from '@/models/launch-calendar-timeline-item.model';
import { OpenAndUpcomingProductModel } from '@/models/open-and-upcoming-product.model';
import { mappersMixins } from '@/mixins/mappers.mixins';
import { utilsMixins } from '@/mixins/utils.mixins';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import moment from 'moment';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import { useDownload } from '@/composables/download';
import { useAnalytics } from '@/composables/analytics';
import { useI18n } from 'vue-i18n';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import { PopUpType } from '@/models/pop-up-type.enum';
import { onClickOutside } from '@vueuse/core';
import { Icons } from '@/models/icons.enum';

const $router = useRouter();
const { t } = useI18n();
const { downloadXlsxFile } = useDownload();
const { addCustomGaEvent } = useAnalytics();
const { selectedAccountId, selectedAccount } = storeToRefs(useUserStore());
const loading = ref(true);
const allowableAssortmentService = new AllowableAssortmentService();
const pricatService = new PricatService();
const productMainGroups = Object.values(ProductMainGroups);
const selectedMainGroup = ref(ProductMainGroups.shoes);
const selectedSeason = ref('');
const seasons = ref(new Array<string>());
const groupedProducts = ref({} as LaunchCalendarModel);
const fileDownloadError = ref<string | undefined>(undefined);
const showErrorPopup = ref(false);
const isPricatDownloadEnabled = import.meta.env.VITE_APP_pricatDownloadEnabled === 'true';
const showPricatContainer = ref(false);
const downloadingPricat = ref(false);
const targetPricatButton = ref(null);

watch(
  () => selectedAccountId.value,
  async (newVal) => {
    if (newVal) {
      loading.value = true;
      await fetchOpenAndUpcomingSeasonAssortments();
    }
  },
);

onMounted(async () => {
  await fetchOpenAndUpcomingSeasonAssortments();
});

onClickOutside(targetPricatButton, () => {
  showPricatContainer.value = false;
});

const launchDateProducts = computed((): LaunchCalendarTimelineItemModel => {
  return groupedProducts.value?.[selectedSeason.value]?.[selectedMainGroup.value] || {};
});
const sortedLaunchDates = computed((): string[] => {
  return Object.keys(launchDateProducts.value).sort(utilsMixins.compareDates);
});
const hasSelectedSeasonProducts = computed((): boolean => {
  return (
    groupedProducts.value?.[selectedSeason.value] &&
    Object.keys(groupedProducts.value[selectedSeason.value])?.length !== 0
  );
});
const hasSelectedMainGroupProducts = computed((): boolean => {
  return Object.keys(launchDateProducts.value).length !== 0;
});

const getIcon = computed(() => {
  return downloadingPricat.value ? Icons.LOADING : Icons.DOWNLOAD_ARROW_SIGN;
});

async function downloadPricat(season: string) {
  downloadingPricat.value = true;
  const response = await pricatService.downloadPricat(selectedAccountId.value, season);

  if (response.isValid() && response.model) {
    try {
      const base64Body = response.model.base64Body;
      downloadXlsxFile(base64Body, `${selectedAccount.value.name}-pricat-${season}`);
    } catch (err: any) {
      fileDownloadError.value = err?.message.toString();
      addCustomGaEvent({
        event: GaCustomEvents.FileDownloadError,
        link_text: '',
      });

      downloadingPricat.value = false;
      showErrorPopup.value = true;
    }

    downloadingPricat.value = false;
  } else {
    fileDownloadError.value = t('documentsCheckout.document_is_not_available');
    addCustomGaEvent({
      event: GaCustomEvents.FileDownloadError,
      link_text: '',
    });
    downloadingPricat.value = false;
    showErrorPopup.value = true;
  }
}

async function fetchOpenAndUpcomingSeasonAssortments() {
  const productsPerSeasons = await allowableAssortmentService.getOpenAndUpcomingSeasonAssortment(
    selectedAccountId.value,
  );

  loading.value = false;
  seasons.value = [];
  groupedProducts.value = {};

  productsPerSeasons.forEach((productsPerSeason) => {
    seasons.value.push(productsPerSeason.season.name);
    groupedProducts.value[productsPerSeason.season.name] = groupProducts(productsPerSeason.products);
  });

  selectedSeason.value = preselectSeason(productsPerSeasons);
}

function initializeMainGroupGrouping() {
  const productMainGroupMap: Record<string, LaunchCalendarTimelineItemModel> = {};

  Object.values(ProductMainGroups).forEach((mainGroup) => {
    productMainGroupMap[mainGroup] = {};
  });

  return productMainGroupMap;
}

function groupProducts(products: OpenAndUpcomingProductItemDto[]) {
  const productMainGroupMap = initializeMainGroupGrouping();

  products.forEach((product) => {
    if (!productMainGroupMap[product.productTypeName][product.productPhaseInDate]) {
      productMainGroupMap[product.productTypeName][product.productPhaseInDate] = {};
    }

    if (!productMainGroupMap[product.productTypeName][product.productPhaseInDate][product.productModelName]) {
      productMainGroupMap[product.productTypeName][product.productPhaseInDate][product.productModelName] = [];
    }

    const matchedProducts =
      productMainGroupMap[product.productTypeName][product.productPhaseInDate][product.productModelName];

    productMainGroupMap[product.productTypeName][product.productPhaseInDate][product.productModelName] = [
      ...matchedProducts,
      mapLaunchCalendarProductModel(product),
    ];
  });

  return productMainGroupMap;
}

function preselectSeason(openAndUpcomingSeasons: OpenAndUpcomingSeasonAssortmentDto[]) {
  const currentDate = moment();

  let activeSellInPeriodSeasonIndex = openAndUpcomingSeasons.findIndex((productSeasonGroup) => {
    // Shoes are the primary assortments and hence we check the shoe sell-in period
    const shoeSellInPeriod = productSeasonGroup.season.seasonSellInPeriods.find(
      (sellInPeriod) => sellInPeriod.productType.name.toLowerCase() === ProductMainGroups.shoes,
    );

    if (shoeSellInPeriod) {
      return (
        moment(shoeSellInPeriod.startDate).startOf('day') <= currentDate &&
        moment(shoeSellInPeriod.endDate).endOf('day') >= currentDate
      );
    }
  });

  // Choose the first season from the response if an active shoe sell-in period is not found in a season
  if (activeSellInPeriodSeasonIndex === -1) {
    activeSellInPeriodSeasonIndex = 0;
  }

  return openAndUpcomingSeasons[activeSellInPeriodSeasonIndex]?.season?.name || '';
}

function mapLaunchCalendarProductModel(product: OpenAndUpcomingProductItemDto) {
  const productModel = new OpenAndUpcomingProductModel();

  productModel.productExternalNumber = product.productExternalNumber;
  productModel.productGender = mappersMixins.genderValueToShortString(product.productGender);
  productModel.productImageUrl = product.productImageUrl;
  productModel.productModelName = product.productModelName;
  productModel.productName = product.productName;
  productModel.productPhaseInDate = product.productPhaseInDate;
  productModel.productTypeName = product.productTypeName;
  productModel.productInitialPhaseInDate = product.productInitialPhaseInDate;

  return productModel;
}

function selectMainGroup(mainGroup: ProductMainGroups) {
  selectedMainGroup.value = mainGroup;
}

function selectSeason(season: string) {
  selectedSeason.value = season;
}

function handleBackButton() {
  $router.go(-1);
}

function closePopup() {
  showErrorPopup.value = false;
}
</script>

<template>
  <div class="view-launch-calendar-cnt">
    <div class="title-panel">
      <div class="title-panel">
        <BackstageIcon
          name="arrow-left"
          class="back-icon"
          @click="handleBackButton"
          data-cy="launch-overview-back-button" />
        <h3 class="title">
          {{ $t('viewLaunchCalendar.launch_calendar_title') }}
        </h3>
      </div>
      <div v-if="seasons.length > 0 && isPricatDownloadEnabled" class="pricat-download" data-cy="pricat-download">
        <BackstageButton
          class="pricat-button"
          :disabled="downloadingPricat"
          ref="targetPricatButton"
          name="arrow-down"
          :icon-right="getIcon"
          data-cy="launch-overview-pricat-button-cnt"
          data-cy-button="launch-overview-pricat-button"
          @click="() => (showPricatContainer = !showPricatContainer)">
          {{ $t('viewLaunchCalendar.pricat.download_pricat_button') }}
        </BackstageButton>
        <div class="pricat-dropdown-container" data-cy="pricat-dropdown-container" v-if="showPricatContainer">
          <div>
            <ul class="pricat-list">
              <li v-for="season in seasons" :key="season">
                <button class="pricat-list-item" variant="secondary" v-on:click="downloadPricat(season)">
                  {{ $t('viewLaunchCalendar.pricat.download_pricat_button') }} {{ season }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <NotificationMessage
        v-if="showErrorPopup"
        :type="PopUpType.Error"
        :title="fileDownloadError"
        v-on:close="closePopup()">
      </NotificationMessage>
    </div>
    <div v-if="loading" class="loader"></div>
    <div v-else>
      <div class="launch-calendar-actions">
        <div class="nav-pills">
          <nav>
            <a
              v-for="productMainGroup in productMainGroups"
              :key="productMainGroup"
              class="link"
              :class="{ selected: productMainGroup === selectedMainGroup }"
              :data-cy="`launch-calendar-${productMainGroup}`"
              @click="selectMainGroup(productMainGroup)">
              <span>{{ $t(`viewLaunchCalendar.${productMainGroup}`) }}</span>
            </a>
          </nav>
        </div>
        <div class="nav-pills">
          <nav>
            <a
              v-for="season in seasons"
              :key="season"
              class="link"
              :class="{ selected: season === selectedSeason }"
              :data-cy="`launch-calendar-${season}`"
              @click="selectSeason(season)">
              <span>{{ $t(`viewLaunchCalendar.seasons.${season}`) }}</span>
            </a>
          </nav>
        </div>
      </div>
      <div v-if="hasSelectedSeasonProducts" class="season-title">
        <h3 class="title">
          {{ $t(`viewLaunchCalendar.seasons.${selectedSeason}_title`) }}
        </h3>
        <h3 class="title sub-title">
          {{ `(${$t('viewLaunchCalendar.season_subtitle')})` }}
        </h3>
      </div>
      <div class="timeline-cnt">
        <div v-if="!hasSelectedMainGroupProducts" class="no-items">
          <p>{{ $t(`viewLaunchCalendar.no_products`) }}</p>
        </div>
        <div v-else class="timeline">
          <launch-calendar-timeline-item
            v-for="launchDate in sortedLaunchDates"
            :key="launchDate"
            :launch-date="launchDate"
            :product-models="groupedProducts[selectedSeason][selectedMainGroup][launchDate]" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.view-launch-calendar-cnt {
  @include common-container;
  @include container-background;
  position: relative;

  // Always show the scroll bar wherever there's a horizontal scroll
  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
    height: 6px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: #7d7d7d;
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }

  .title {
    font-size: 1.25rem;
    font-weight: 400;
    padding-left: 10px;
  }

  .launch-calendar-actions {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;

    nav {
      a {
        padding: 0px 50px;
      }
    }
  }

  .season-title h3 {
    text-align: center;
    margin-bottom: 10px;
    padding-top: 20px;
  }

  .sub-title {
    color: var(--field-text-disabled-color);
    margin-top: 0px;
  }

  .timeline-cnt {
    margin: auto;
    padding: 10px;

    .timeline {
      display: flex;
      overflow-x: scroll;
      width: auto;
      flex-direction: row;
      padding-bottom: 30px;
    }

    .no-items {
      min-height: 150px;
      margin: auto;
    }
  }

  .back-icon {
    width: 26px;
    height: 26px;
    padding: 15px;
    cursor: pointer;
  }

  .title-panel {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding-right: 20px;
  }

  .launch-calendar-actions:hover + .pricat-download.pricat-dropdown-container {
    opacity: 1;
  }

  .pricat-download {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
    max-width: 90%;

    .pricat-button {
      width: 26px;
      height: 26px;
      padding: 15px;
      cursor: pointer;
    }

    .pricat-dropdown-container {
      z-index: 0;
      // display: none;
      margin-top: 45px;
      margin-right: 40px;
      position: absolute;
      right: 0;
      width: 13.625rem;
      border-radius: 0.4rem;
      overflow: hidden;
      box-shadow: 0px 5px 5px 0px rgba(0, 0, 0, 0.1);

      .pricat-list {
        padding: 0px;
        margin: 0;
        list-style: none;
        text-align: left;

        .pricat-list-item {
          border: none;
          width: 100%;
          padding: 0.825rem;
          text-align: left;
          border-bottom: 1px solid;
          border-bottom-color: var(--bg-remark-selected-color);
          background-color: var(--primary-bg-color);
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 400;
          &.active,
          &:hover {
            color: var(--primary-text-color);
            background-color: var(--bg-remark-selected-color);

            @include breakpoint(medium) {
              color: var(--primary-text-color);
            }
          }
        }

        li:first-child .pricat-list-item {
          border-top-left-radius: 0.6rem;
          border-top-right-radius: 0.6rem;
        }
        li:last-child .pricat-list-item {
          border-bottom-left-radius: 0.6rem;
          border-bottom-right-radius: 0.6rem;
        }
      }
    }

    &:hover {
      .pricat-dropdown-container {
        display: block;
      }
    }

    @media (hover) {
      &:hover {
        .pricat-list {
          display: block;
        }
      }
    }

    @media (hover: none) {
      &.hovered {
        .pricat-list {
          display: block;
        }
      }
    }
  }
}
</style>
